import { z } from 'zod';
import { SECTION_TYPES } from '../constants.js';
import { linkSchema, optionalText, requiredText } from './common.js';

/**
 * Section payloads — one schema per member of `SECTION_TYPES`.
 *
 * A page is an ordered list of `{ type, payload }`. The payload is stored as JSONB and
 * validated here on the way in, which is what lets the shape of a section change
 * without a migration while still refusing nonsense at the door.
 *
 * The discriminated union at the bottom is the contract. `sectionPayloadSchema` is how
 * the API validates a section it is asked to save, and the same union narrows the
 * payload type inside each React component, so a component can never read a field the
 * schema does not guarantee.
 *
 * **Sections that read from a table hold only selection.** `product_grid` stores "six
 * products, category `receipt-printers`, newest first" — not six product records. A
 * price edited in the store is immediately right everywhere it appears, and there is no
 * second copy to go stale. The single exception is `stats`, whose figures are typed by
 * hand because they are claims about the business, not rows in a table.
 */

/* -------------------------------------------------------------------------- */
/* Shared fragments                                                            */
/* -------------------------------------------------------------------------- */

/** A button. `style` picks the treatment; the palette itself lives in the theme. */
export const ctaSchema = z.object({
  label: requiredText(60, 'Button text'),
  href: linkSchema,
  style: z.enum(['primary', 'secondary', 'ghost']).default('primary'),
});
export type Cta = z.infer<typeof ctaSchema>;

export const mediaRefSchema = z.object({
  url: z.string().trim().min(1).max(500),
  alt: optionalText(200),
});

/** Heading block shared by most sections. Every field optional — a section may be all list. */
const headingFragment = {
  eyebrow: optionalText(80),
  heading: optionalText(200),
  body: optionalText(1200),
};

/**
 * How many records to pull, for the sections that read a table.
 *
 * Capped at 24. A "grid" of every product ever sold is not a section, it is the shop
 * page, and rendering it inside a home page is how a landing page ends up at 4MB.
 */
const limitSchema = z.coerce.number().int().min(1).max(24);

/**
 * How a section is FRAMED on the page, as opposed to what it contains.
 *
 * `panel` is the raised card on the canvas: the default, and what every section was
 * before this existed. `open` drops the card — no background, no shadow, no radius —
 * and lets the section sit directly on the page ground.
 *
 * It is per-section rather than per-type on purpose. Rhythm is a property of the
 * ORDER sections appear in, not of what any one of them is: two open sections next to
 * each other read as one undifferentiated stretch, and a type-level rule would produce
 * exactly that the first time an editor reorders the page. Only a human looking at the
 * result can judge it, so a human is who sets it.
 *
 * Sections with no outer panel to remove -- `marquee`, `stats` -- do not carry the
 * field, so the editor never shows a control that would do nothing.
 */
export const groundSchema = z.enum(['panel', 'open']).default('panel');
const framingFragment = { ground: groundSchema };

/* -------------------------------------------------------------------------- */
/* Payloads                                                                    */
/* -------------------------------------------------------------------------- */

export const heroPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  primaryCta: ctaSchema.optional().nullable(),
  secondaryCta: ctaSchema.optional().nullable(),
  media: mediaRefSchema.optional().nullable(),
  /**
   * `counter` swaps the picture for the animated point-of-sale panel. It is a
   * deliberate option rather than the only hero: the animation is the right answer on
   * the home page and the wrong one on `About`, where a photograph of the office says
   * more and costs nothing to render.
   */
  visual: z.enum(['image', 'counter', 'none']).default('image'),
  /**
   * A short row of figures under the buttons — years, installs, parts held, response
   * time. Optional, and capped at four: this is the first screen, and a fifth number
   * turns a claim into a table nobody reads.
   */
  facts: z
    .array(z.object({ value: requiredText(16, 'Value'), label: requiredText(40, 'Label') }))
    .max(4)
    .optional(),
});

export const marqueePayloadSchema = z.object({
  items: z.array(requiredText(80, 'Phrase')).min(1).max(40),
  speedSeconds: z.coerce.number().int().min(10).max(240).default(46),
});

export const trustBarPayloadSchema = z.object({
  ...framingFragment,
  label: optionalText(60),
  names: z.array(requiredText(60, 'Name')).min(1).max(12),
});

export const capabilitiesPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  items: z
    .array(
      z.object({
        title: requiredText(120, 'Title'),
        body: requiredText(900, 'Body'),
        tags: z.array(requiredText(40, 'Tag')).max(8).default([]),
      }),
    )
    .min(1)
    .max(12),
});

export const servicesGridPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  limit: limitSchema.default(6),
  columns: z.coerce.number().int().min(2).max(4).default(3),
  ctaLabel: optionalText(60),
});

export const featuredProjectPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  /**
   * Null means "whichever project carries the featured flag", which is the setting the
   * home page ships with: the flag is set once in Projects and the home page follows it
   * for ever. Naming a project id here pins THIS page to THAT project regardless of the
   * flag — useful on a service page that should always show its own case study.
   */
  projectId: z.coerce.number().int().positive().optional().nullable(),
  ctaLabel: optionalText(60),
  layout: z.enum(['image-left', 'image-right']).default('image-left'),
});

export const projectGridPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  limit: limitSchema.default(6),
  columns: z.coerce.number().int().min(2).max(4).default(3),
  /** Null shows every published project; a slug narrows it to one service line. */
  serviceSlug: optionalText(120),
  /**
   * Kept out by default. The featured project already has a section of its own above,
   * and showing it again immediately underneath reads as a rendering fault.
   */
  excludeFeatured: z.boolean().default(true),
  ctaLabel: optionalText(60),
  ctaHref: linkSchema.optional().nullable(),
});

export const productGridPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  limit: limitSchema.default(4),
  columns: z.coerce.number().int().min(2).max(4).default(4),
  categorySlug: optionalText(120),
  brandSlug: optionalText(120),
  /** `featured` reads the flag on the product; the rest are plain orderings. */
  source: z.enum(['featured', 'newest', 'price-asc', 'price-desc']).default('featured'),
  /** Off by default on a landing page: browsing is the goal there, not checkout. */
  showAddToCart: z.boolean().default(true),
  ctaLabel: optionalText(60),
  ctaHref: linkSchema.optional().nullable(),
});

export const statsPayloadSchema = z.object({
  ...headingFragment,
  /**
   * None, or at least two — never exactly one.
   *
   * The floor of two exists because a stats band showing a single figure reads as a
   * layout fault rather than a design. But `.min(2)` also forced the DEFAULT to carry
   * two figures, and a default that carries figures means invented ones: this section's
   * default used to claim "250+ completed builds", ready to be published by an editor
   * who never typed it. Allowing an empty list lets the default assert nothing and the
   * section render nothing until someone enters real numbers.
   */
  items: z
    .array(
      z.object({
        value: requiredText(20, 'Figure'),
        label: requiredText(80, 'Caption'),
      }),
    )
    .max(6)
    .refine((items) => items.length === 0 || items.length >= 2, {
      message: 'Add at least two figures, or none at all — a single figure looks like a mistake.',
    }),
});

export const testimonialsPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  limit: limitSchema.default(3),
  layout: z.enum(['carousel', 'grid', 'single']).default('grid'),
});

export const teamPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  limit: limitSchema.default(6),
  columns: z.coerce.number().int().min(2).max(4).default(3),
  showBio: z.boolean().default(true),
});

export const milestonesPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  limit: limitSchema.default(8),
});

export const imageTextPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  /**
   * Nullable, because a section is ADDED before it is filled in.
   *
   * Requiring the image here made the blank default invalid, so adding an "Image +
   * text" section produced a validation error on a form the editor had not yet
   * touched — and the only way out was to work out which invisible field was wrong.
   * The component renders the text alone when there is no picture.
   */
  media: mediaRefSchema.optional().nullable(),
  imageSide: z.enum(['left', 'right']).default('left'),
  bullets: z.array(requiredText(200, 'Point')).max(8).default([]),
  cta: ctaSchema.optional().nullable(),
});

/* -------------------------------------------------------------------------- */
/* The configurator                                                            */
/* -------------------------------------------------------------------------- */

/**
 * One choice within one configurator option.
 *
 * `price` is per LANE for anything the lane multiplier applies to, and a flat figure
 * otherwise — `perLane` decides which. That distinction is the whole arithmetic: a
 * second checkout lane needs a second screen and a second printer, and does not need a
 * second copy of the software licence.
 */
export const configuratorChoiceSchema = z.object({
  value: requiredText(40, 'Value'),
  label: requiredText(60, 'Label'),
  price: z.coerce.number().min(0).default(0),
  perLane: z.boolean().default(false),
  /** Shown on the bill of materials instead of the label, when the two differ. */
  billLabel: optionalText(80),
});

export const configuratorPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  note: optionalText(300),
  options: z
    .array(
      z.object({
        key: requiredText(40, 'Key'),
        label: requiredText(80, 'Label'),
        note: optionalText(300),
        /**
         * Which choice is selected before anyone touches it.
         *
         * Explicit rather than "the first one", because the sensible default is often
         * in the middle: the counter opens on the dual display, which is the option
         * most customers end up buying, while the list still reads small → large.
         * An unknown or absent value falls back to the first choice.
         */
        defaultValue: optionalText(40),
        choices: z.array(configuratorChoiceSchema).min(2).max(6),
      }),
    )
    .min(1)
    .max(10),
  /** Charged once, plus a per-lane amount for the extra fitting time. */
  installBase: z.coerce.number().min(0).default(0),
  installPerLane: z.coerce.number().min(0).default(0),
  installLabel: optionalText(80),
  maxLanes: z.coerce.number().int().min(1).max(24).default(8),
  buildWeeks: z.coerce.number().int().min(1).max(52).default(6),
  totalLabel: optionalText(80),
  ctaLabel: optionalText(60),
  /**
   * Show the 3D counter above and drive it from the choices.
   *
   * The model is roughly 600 kB of Three.js, loaded lazily and only when this is on —
   * it is the most expensive thing on the site and it is worth it exactly once, on the
   * home page, where it is the argument the page is making.
   */
  drivesModel: z.boolean().default(true),
});

/* -------------------------------------------------------------------------- */
/* Practice                                                                    */
/* -------------------------------------------------------------------------- */

export const practicePayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  items: z
    .array(z.object({ title: requiredText(120, 'Title'), body: requiredText(900, 'Body') }))
    .min(1)
    .max(8),
  /** The headline figures, inside the same dark panel rather than a section of their own. */
  facts: z
    .array(z.object({ value: requiredText(20, 'Figure'), label: requiredText(80, 'Caption') }))
    .max(6)
    .default([]),
});

/* -------------------------------------------------------------------------- */
/* Deployment record                                                           */
/* -------------------------------------------------------------------------- */

export const deploymentRecordPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  /** Null follows the featured project; an id pins this panel to one case study. */
  projectId: z.coerce.number().int().positive().optional().nullable(),
  media: mediaRefSchema.optional().nullable(),
  mediaCaption: optionalText(160),
  timeline: z
    .array(z.object({ when: requiredText(30, 'When'), what: requiredText(400, 'What happened') }))
    .max(12)
    .default([]),
  quote: optionalText(600),
  quoteAttribution: optionalText(160),
  ctaLabel: optionalText(60),
  ctaHref: linkSchema.optional().nullable(),
});

/* -------------------------------------------------------------------------- */
/* Contact                                                                     */
/* -------------------------------------------------------------------------- */

export const contactSplitPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  source: z.enum(['CONTACT', 'QUOTE', 'PROJECT_ENQUIRY']).default('CONTACT'),
  needsOptions: z.array(requiredText(60, 'Option')).max(10).default([]),
  submitLabel: optionalText(60),
  successMessage: optionalText(400),
  /** Right-hand cards. Drawn from settings when empty, so the address is not typed twice. */
  showOffice: z.boolean().default(true),
  showPhone: z.boolean().default(true),
  showEmail: z.boolean().default(true),
  footnote: optionalText(300),
  /**
   * The ground this section sits on.
   *
   * Exists so a page can END on something other than its seventh identical white panel.
   * The home page stacks eight sections in the same pale card, which reads as a list
   * rather than a composition and leaves the primary call to action no more prominent
   * than the marquee. `dark` makes the closing block the heaviest thing on the page.
   *
   * `light` stays the default: on `/contact` and `/quote` this section IS the page, and
   * a full-height dark form is a different and worse decision.
   */
  tone: z.enum(['light', 'dark']).default('light'),
});

export const richTextPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  /** Markdown, rendered by a hardened renderer — never `dangerouslySetInnerHTML`. §9. */
  markdown: requiredText(20_000, 'Body'),
  width: z.enum(['narrow', 'wide']).default('narrow'),
});

export const faqPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  items: z
    .array(
      z.object({
        question: requiredText(200, 'Question'),
        answer: requiredText(2000, 'Answer'),
      }),
    )
    .min(1)
    .max(30),
});

export const ctaSplitPayloadSchema = z.object({
  ...framingFragment,
  panels: z
    .array(
      z.object({
        heading: requiredText(120, 'Heading'),
        body: optionalText(400),
        cta: ctaSchema,
        media: mediaRefSchema.optional().nullable(),
        tone: z.enum(['dark', 'light', 'accent']).default('dark'),
      }),
    )
    .length(2, 'A split call to action has exactly two panels'),
});

export const ctaBannerPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  cta: ctaSchema,
  tone: z.enum(['dark', 'light', 'accent']).default('accent'),
});

export const contactFormPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  /** Which enquiry list submissions land in. */
  source: z.enum(['CONTACT', 'QUOTE', 'PROJECT_ENQUIRY']).default('CONTACT'),
  showCompany: z.boolean().default(true),
  showPhone: z.boolean().default(true),
  showBudget: z.boolean().default(false),
  serviceOptions: z.array(requiredText(80, 'Option')).max(12).default([]),
  submitLabel: optionalText(60),
  successMessage: optionalText(400),
});

export const newsletterPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  placeholder: optionalText(80),
  submitLabel: optionalText(40),
  successMessage: optionalText(200),
});

export const logoWallPayloadSchema = z.object({
  ...framingFragment,
  ...headingFragment,
  logos: z
    .array(
      z.object({
        name: requiredText(80, 'Name'),
        imageUrl: optionalText(500),
        href: linkSchema.optional().nullable(),
      }),
    )
    .min(1)
    .max(24),
  grayscale: z.boolean().default(true),
});

/* -------------------------------------------------------------------------- */
/* The union                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Every section type maps to exactly one payload schema.
 *
 * Typed as a full `Record` over `SectionType` on purpose: adding a member to
 * `SECTION_TYPES` without adding its schema here is a TypeScript error at this line,
 * rather than a runtime `undefined` the first time an editor picks the new section.
 */
export const SECTION_PAYLOAD_SCHEMAS = {
  hero: heroPayloadSchema,
  marquee: marqueePayloadSchema,
  trust_bar: trustBarPayloadSchema,
  capabilities: capabilitiesPayloadSchema,
  configurator: configuratorPayloadSchema,
  practice: practicePayloadSchema,
  deployment_record: deploymentRecordPayloadSchema,
  contact_split: contactSplitPayloadSchema,
  services_grid: servicesGridPayloadSchema,
  featured_project: featuredProjectPayloadSchema,
  project_grid: projectGridPayloadSchema,
  product_grid: productGridPayloadSchema,
  stats: statsPayloadSchema,
  testimonials: testimonialsPayloadSchema,
  team: teamPayloadSchema,
  milestones: milestonesPayloadSchema,
  image_text: imageTextPayloadSchema,
  rich_text: richTextPayloadSchema,
  faq: faqPayloadSchema,
  cta_split: ctaSplitPayloadSchema,
  cta_banner: ctaBannerPayloadSchema,
  contact_form: contactFormPayloadSchema,
  newsletter: newsletterPayloadSchema,
  logo_wall: logoWallPayloadSchema,
} as const satisfies Record<(typeof SECTION_TYPES)[number], z.ZodTypeAny>;

/** Discriminated union of `{ type, payload }`, used to save and to render. */
export const sectionContentSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('hero'), payload: heroPayloadSchema }),
  z.object({ type: z.literal('marquee'), payload: marqueePayloadSchema }),
  z.object({ type: z.literal('trust_bar'), payload: trustBarPayloadSchema }),
  z.object({ type: z.literal('capabilities'), payload: capabilitiesPayloadSchema }),
  z.object({ type: z.literal('configurator'), payload: configuratorPayloadSchema }),
  z.object({ type: z.literal('practice'), payload: practicePayloadSchema }),
  z.object({ type: z.literal('deployment_record'), payload: deploymentRecordPayloadSchema }),
  z.object({ type: z.literal('contact_split'), payload: contactSplitPayloadSchema }),
  z.object({ type: z.literal('services_grid'), payload: servicesGridPayloadSchema }),
  z.object({ type: z.literal('featured_project'), payload: featuredProjectPayloadSchema }),
  z.object({ type: z.literal('project_grid'), payload: projectGridPayloadSchema }),
  z.object({ type: z.literal('product_grid'), payload: productGridPayloadSchema }),
  z.object({ type: z.literal('stats'), payload: statsPayloadSchema }),
  z.object({ type: z.literal('testimonials'), payload: testimonialsPayloadSchema }),
  z.object({ type: z.literal('team'), payload: teamPayloadSchema }),
  z.object({ type: z.literal('milestones'), payload: milestonesPayloadSchema }),
  z.object({ type: z.literal('image_text'), payload: imageTextPayloadSchema }),
  z.object({ type: z.literal('rich_text'), payload: richTextPayloadSchema }),
  z.object({ type: z.literal('faq'), payload: faqPayloadSchema }),
  z.object({ type: z.literal('cta_split'), payload: ctaSplitPayloadSchema }),
  z.object({ type: z.literal('cta_banner'), payload: ctaBannerPayloadSchema }),
  z.object({ type: z.literal('contact_form'), payload: contactFormPayloadSchema }),
  z.object({ type: z.literal('newsletter'), payload: newsletterPayloadSchema }),
  z.object({ type: z.literal('logo_wall'), payload: logoWallPayloadSchema }),
]);

export type SectionContent = z.infer<typeof sectionContentSchema>;

/**
 * Validate a payload against the schema for one type.
 *
 * Returns the parsed payload or throws a `ZodError`, which the API's error handler
 * turns into `400 ERR_VALIDATION`. Used where the type arrives separately from the
 * payload (the section editor PATCHes one field at a time).
 */
export function parseSectionPayload(type: string, payload: unknown): unknown {
  const schema = SECTION_PAYLOAD_SCHEMAS[type as keyof typeof SECTION_PAYLOAD_SCHEMAS];
  if (!schema) throw new Error('ERR_UNKNOWN_SECTION_TYPE');
  return schema.parse(payload);
}

/**
 * The allowed values for one payload field, when the schema pins it to a fixed set.
 *
 * The admin's section editor chooses a control from the RUNTIME TYPE of the value it
 * finds, which is what keeps every section type editable without a bespoke form each.
 * The cost is that an enum is indistinguishable from free text: `ground` and `tone`
 * rendered as a plain input, where a typo is not caught until the save fails
 * validation, and where nothing tells the editor what the choices were. This reads the
 * choices back off the schema so the editor can offer them.
 *
 * Returns `null` for every field that is not an enum, which is the signal to fall back
 * to shape-driven control picking.
 */
export function sectionFieldOptions(type: string, key: string): readonly string[] | null {
  const schema = SECTION_PAYLOAD_SCHEMAS[type as keyof typeof SECTION_PAYLOAD_SCHEMAS];
  if (!schema) return null;

  let field: z.ZodTypeAny | undefined = (schema.shape as Record<string, z.ZodTypeAny>)[key];

  // `.default()`, `.optional()` and `.nullable()` each wrap the schema they modify, so
  // the enum is not the outermost type. Unwrap until it is. The depth guard is
  // paranoia about a self-referential schema, not a case anyone has hit.
  for (let depth = 0; field && depth < 5; depth++) {
    if (field instanceof z.ZodEnum) return field.options as readonly string[];
    const inner = (field._def as { innerType?: z.ZodTypeAny }).innerType;
    if (!inner) return null;
    field = inner;
  }
  return null;
}

/** Payload fields that describe FRAMING rather than content, in the order to show them. */
export const SECTION_LAYOUT_KEYS = ['tone', 'ground', 'grayscale'] as const;

/**
 * The fields to show for a section, in the order to show them.
 *
 * Two problems with reading the stored payload directly, which is what the editor did:
 *
 * 1. A control appears only for a key the row already HAS. A field added to the schema
 *    after a row was written -- `ground`, on every section that predates it -- would
 *    have no control, and so could never be set from the one screen that exists to set
 *    it. Parsing fills in whatever the schema defaults, which is exactly the set of
 *    fields that always apply.
 *
 *    No VALUE is invented by this. A switch arrives at its declared default, and an
 *    absent optional text arrives as `null` -- `optionalText` normalises `undefined`
 *    to `null` -- which renders as an empty input. That is the point: a heading the
 *    row was never given is a heading an editor could not previously add.
 *
 * 2. Insertion order put framing above content. An editor opens this to change a
 *    heading far more often than to change how a section is mounted.
 *
 * A payload that does not parse is returned exactly as stored. That is the case where
 * an editor most needs to see the real contents and least needs them rewritten.
 */
export function sectionEditorFields(type: string, payload: Record<string, unknown>): [string, unknown][] {
  let filled = payload;
  try {
    filled = { ...(parseSectionPayload(type, payload) as Record<string, unknown>), ...payload };
  } catch {
    /* not parseable: show it exactly as stored */
  }

  const rank = (key: string) => (SECTION_LAYOUT_KEYS as readonly string[]).indexOf(key);
  return Object.entries(filled).sort(([a], [b]) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra === -1 && rb === -1) return 0; // both content: leave as they came
    if (ra === -1) return -1;
    if (rb === -1) return 1;
    return ra - rb;
  });
}

/**
 * A blank, VALID payload for a newly added section.
 *
 * Every one of these parses against its own schema — there is a test that asserts it
 * for every one of them. A default that fails validation means an editor adds a section and
 * cannot save the page until they work out which invisible field is wrong.
 */
export const SECTION_DEFAULTS: { [K in keyof typeof SECTION_PAYLOAD_SCHEMAS]: z.input<(typeof SECTION_PAYLOAD_SCHEMAS)[K]> } = {
  hero: {
    eyebrow: 'Software · Point of sale · Hardware',
    heading: 'From the schema to the counter.',
    body: 'We specify, write and install the systems Sri Lankan operators bill on.',
    primaryCta: { label: 'Start a project', href: '/contact', style: 'primary' },
    secondaryCta: { label: 'See what we do', href: '/services', style: 'secondary' },
    media: null,
    visual: 'counter',
  },
  marquee: {
    items: ['Point of sale', 'Thermal printers', 'Barcode scanners', 'Cash drawers', 'Networking', 'On-site SLA'],
    speedSeconds: 46,
  },
  trust_bar: { label: 'Trusted by operators at', names: ['Salon Cleo', 'Homagama Foods', 'Araliya Retail'] },
  capabilities: {
    heading: 'What we do',
    items: [{ title: 'Custom software development', body: 'Describe the capability here.', tags: [] }],
  },
  configurator: {
    eyebrow: 'Live configurator — drives the model above',
    heading: 'Build your counter, see the bill',
    note: 'Computed from the same price table our written quotes are generated from.',
    options: [
      {
        key: 'terminal',
        label: 'Terminal',
        note: 'Screen size and form factor at each lane.',
        defaultValue: 'dual',
        choices: [
          { value: 'single', label: '15in single screen', price: 178000, perLane: true, billLabel: '15in single screen' },
          { value: 'dual', label: '15.6in dual display', price: 245000, perLane: true, billLabel: '15.6in dual display' },
          { value: 'handheld', label: 'Handheld Android', price: 96000, perLane: true, billLabel: 'Handheld Android' },
        ],
      },
      {
        key: 'software',
        label: 'Software scope',
        note: 'What the system has to do on day one.',
        defaultValue: 'stock',
        choices: [
          { value: 'billing', label: 'Billing only', price: 165000, perLane: false, billLabel: 'Billing only' },
          { value: 'stock', label: 'Billing + stock', price: 240000, perLane: false, billLabel: 'Billing + stock' },
          { value: 'booking', label: 'Billing + stock + booking', price: 320000, perLane: false, billLabel: 'Billing + stock + booking' },
        ],
      },
      {
        key: 'printer',
        label: 'Thermal receipt printer',
        note: '80mm auto-cutter, paired and tested before delivery.',
        choices: [
          { value: 'include', label: 'Include', price: 58500, perLane: true, billLabel: 'Thermal printer, 80mm' },
          { value: 'skip', label: 'Skip', price: 0, perLane: false, billLabel: null },
        ],
      },
      {
        key: 'drawer',
        label: 'Cash drawer',
        note: 'Steel, 5 note / 8 coin, triggered by the printer.',
        choices: [
          { value: 'include', label: 'Include', price: 31400, perLane: true, billLabel: 'Steel cash drawer' },
          { value: 'skip', label: 'Skip', price: 0, perLane: false, billLabel: null },
        ],
      },
      {
        key: 'scanner',
        label: 'Barcode scanner',
        note: '1D/2D imager on a shielded stand — worth it above ~300 SKUs.',
        choices: [
          { value: 'include', label: 'Include', price: 42000, perLane: true, billLabel: 'Barcode scanner' },
          { value: 'skip', label: 'Skip', price: 0, perLane: false, billLabel: null },
        ],
      },
    ],
    installBase: 21000,
    installPerLane: 10500,
    installLabel: 'Install, cabling and training',
    maxLanes: 8,
    buildWeeks: 6,
    totalLabel: 'Indicative total, excluding VAT',
    ctaLabel: 'Send this configuration',
    drivesModel: true,
  },
  practice: {
    eyebrow: 'How we work',
    // A newline in a heading is rendered as a line break by the component. The design
    // breaks this one deliberately — the two halves are a pair, and letting the
    // viewport decide where to wrap loses the antithesis.
    heading: 'Fast to a build.\nSlow to a shortcut.',
    body: 'Our tooling has changed how quickly we can put something real in front of you. What it has not changed is who is accountable for the schema, the security review and the machine on your counter.',
    items: [
      { title: 'We specify before we build', body: 'Every engagement starts behind your counter, watching the two busiest hours. Scope, timeline and cost are agreed in writing before a line of code is committed.' },
      { title: 'A working build early', body: 'You see something running on your own data in weeks, not at the end. Modern tooling buys us that speed; it is spent on more review cycles with you, not on fewer.' },
      { title: 'Nothing ships unreviewed', body: 'Schema changes, payment paths and tax logic are read line by line by a second engineer and covered by tests before they reach a till. However it was drafted, the bar is the same.' },
      { title: 'We stay after handover', body: 'Preventative checks, consumables held locally and a named engineer who already knows your install. Problems surface as a phone call from us, not as downtime for you.' },
    ],
    /**
     * EMPTY, deliberately.
     *
     * A default must never assert a fact about the business. These carried invented
     * figures — "140+ systems delivered", "99.9% booking uptime" — and a default is
     * pre-filled into the editor the moment somebody adds the section, so the invented
     * numbers could be published by an editor who never typed them and had no reason to
     * doubt them. They reached the live site exactly that way once.
     *
     * The components all guard on length, so an empty list renders nothing until real
     * figures are entered.
     */
    facts: [],
  },
  deployment_record: {
    eyebrow: 'Deployment record',
    projectId: null,
    media: null,
    // Null: a caption here would describe a photograph that does not exist.
    mediaCaption: null,
    timeline: [],
    quote: null,
    quoteAttribution: null,
    ctaLabel: 'Full deployment record',
    ctaHref: null,
  },
  contact_split: {
    eyebrow: 'Start a project',
    heading: 'Tell us what you are building.',
    body: 'Send the brief, even a rough one. You get back a scope, an indicative timeline, and an honest view on whether we are the right team for it.',
    source: 'CONTACT',
    needsOptions: ['Custom software', 'Point of sale', 'Hardware supply', 'Installation', 'Maintenance', 'Something else'],
    submitLabel: 'Send the brief',
    successMessage: 'Thank you — we have your brief and will reply within one business day.',
    showOffice: true,
    showPhone: true,
    showEmail: true,
    footnote: 'We reply within one business day. No mailing list, no follow-up sequence.',
  },
  services_grid: { eyebrow: 'Complete service suite', heading: 'What we build & deploy', limit: 6, columns: 3 },
  featured_project: { eyebrow: 'Case study', projectId: null, layout: 'image-left', ctaLabel: 'View full case study' },
  project_grid: { heading: 'Selected work', limit: 6, columns: 3, excludeFeatured: true },
  product_grid: {
    eyebrow: 'Hardware store',
    heading: 'Featured point-of-sale peripherals',
    limit: 4,
    columns: 4,
    source: 'featured',
    showAddToCart: true,
    ctaLabel: 'View full store',
    ctaHref: '/shop',
  },
  stats: { items: [] },
  testimonials: { eyebrow: 'Testimonial', heading: 'What our clients say', limit: 3, layout: 'grid' },
  team: { eyebrow: 'Expert leadership', heading: 'Meet the team', limit: 6, columns: 3, showBio: true },
  milestones: { heading: 'Company milestones', limit: 8 },
  image_text: {
    heading: 'A heading for this section',
    body: 'Supporting copy.',
    // Null, not an empty url — a section is added before it is filled in.
    media: null,
    imageSide: 'left',
    bullets: [],
    cta: null,
  },
  rich_text: { markdown: 'Write here.', width: 'narrow' },
  faq: { heading: 'Frequently asked', items: [{ question: 'A question?', answer: 'The answer.' }] },
  cta_split: {
    panels: [
      { heading: 'Need custom business software?', body: null, cta: { label: 'Discuss a project', href: '/contact', style: 'primary' }, media: null, tone: 'dark' },
      { heading: 'Ready-to-ship retail hardware', body: null, cta: { label: 'Shop the store', href: '/shop', style: 'secondary' }, media: null, tone: 'light' },
    ],
  },
  cta_banner: { heading: 'Tell us what you are building.', cta: { label: 'Get in touch', href: '/contact', style: 'primary' }, tone: 'accent' },
  contact_form: { heading: 'Tell us what you are building.', source: 'CONTACT', showCompany: true, showPhone: true, showBudget: false, serviceOptions: [] },
  newsletter: { heading: 'Stay updated', placeholder: 'Enter your email', submitLabel: 'Join' },
  logo_wall: { heading: 'Partners', logos: [{ name: 'Partner', imageUrl: null, href: null }], grayscale: true },
};
